import { GeoData, GoLinkData } from '../../entities';
import type { SessionPropsExtension } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { getDocumentReferrer } from '../referrer';

declare const global: {
  geo: GeoData;
};

export const GO_LINK_SLUG_RE = /\/go\/([\w\d-]+)/;

export const extractGoLinkDataFromAnchor = (data: GoLinkData, anchor: HTMLAnchorElement): GoLinkData => {
  const event_action: string | undefined = anchor.getAttribute('data-action') ?? undefined;
  const event_category: string | undefined = anchor.getAttribute('data-category') || 'Lead Click';
  const event_label: string | undefined = anchor.getAttribute('data-label') || 'Page Content Link';
  const partner_slug = anchor.getAttribute('data-partner') ?? undefined;
  const placement_id = anchor.getAttribute('placement-id') ?? undefined;
  const link = anchor.getAttribute('data-link') ?? undefined;
  const author_html = document.querySelectorAll('.author-name')?.[0]?.innerHTML ?? undefined;
  const text = anchor?.innerText;
  return {
    ...data,
    author_html,
    event_action,
    event_category,
    event_label,
    link,
    partner_slug,
    placement_id,
    text,
  };
};

export const url_data = (key: string | null = null) => {
  const urlSearchParams: any = new URLSearchParams(window.location.search) as any;
  const data: any = Object.fromEntries(urlSearchParams.entries());
  const matches = window.location.pathname.match('[A-z0-9-]+/[0-9]+/[0-9]+/([0-9]+)/([A-z0-9-]+)');
  data['path'] = window.location.pathname;
  if (matches && matches[1]) {
    data['node_id'] = matches[1];
    data['node_slug'] = matches[2];
  }
  return key ? data[key] : data;
};

const buildSponsorEventDataForGoLink = (data: GoLinkData): string | undefined => {
  const nested = data.sponsor_details?.event_data?.trim();
  if (nested) return nested;
  const fromApi = data.event_data?.trim();
  if (fromApi) return fromApi;
  return undefined;
};

const generateGoLinkAction = (data: GoLinkData): string | undefined => {
  let action = '';
  if (data.partner && data.name && data.name !== data.partner)
    action = '::Partner:: `' + data.partner + '` ::GoLink:: `' + data.name + '`';
  else if (data.partner) action = '::Partner:: `' + data.partner + '`';
  else if (data.name) action = '::GoLink:: `' + data.name + '`';
  if (data.text) action += ` ::Text:: ${data.text}`;
  return action || undefined;
};

const buildTrackedUrl = (go_link_data: GoLinkData) => {
  const author_name = go_link_data.author_html ? go_link_data.author_html.trim() : null;

  let href = go_link_data.href;
  if (go_link_data.partner_slug && !href) {
    href = `${window.location.protocol}//${window.location.host}/go/${go_link_data.partner_slug}`;
  } else if (go_link_data.link && !href) {
    href = go_link_data.link;
  }

  const params = url_data();

  if (go_link_data?.post_id) params['page_r'] = go_link_data?.post_id;
  if (go_link_data.placement_id) {
    params['pi'] = go_link_data.placement_id;
  }

  const referrer = getDocumentReferrer();
  if (referrer) {
    params['referrer'] = referrer;
  }

  if (go_link_data.event_action) {
    params['event_action'] = go_link_data.event_action;
  }
  if (go_link_data.event_category) {
    params['event_category'] = go_link_data.event_category;
  }
  if (go_link_data.event_label) {
    params['pl'] = go_link_data.event_label;
    params['event_label'] = go_link_data.event_label;
  }
  if (global.geo) params['cc'] = global.geo.country_code;
  if (global.geo?.region) params['region'] = global.geo.region;
  if (global.geo?.city) params['city'] = global.geo.city;
  if (author_name) params['an'] = author_name;
  params['tid'] = (Math.random() + 1).toString(36).substring(2);

  if (window.location.search !== '') {
    const utm_source = new URLSearchParams(window.location.search).get('utm_source');
    if (utm_source && utm_source !== '') {
      params['utm_source'] = utm_source;
    }
  }

  if (!href) return;

  return buildUrl(href, params);
};

const buildUrl = (href: string, params: any) => {
  const parser = document.createElement('a');
  parser.href = href;

  const esc = encodeURIComponent;
  const query = Object.keys(params)
    .map(k => esc(k) + '=' + esc(params[k]))
    .join('&');

  return parser.search.length ? parser.href + '&' + query : parser.href + '?' + query;
};

const getTrackingParamForSubId = (paidMediaData: any) => {
  const url = window?.location?.search;
  const params = new URLSearchParams(url);
  const subId = params.get('subid1');
  if (
    subId &&
    subId !== '' &&
    paidMediaData?.enable_tracking &&
    paidMediaData?.conversion_destinations &&
    paidMediaData?.conversion_destinations.length > 0
  ) {
    const conversion_data = paidMediaData.conversion_destinations.find((dest: any) => dest.subid1 === subId);
    return conversion_data ? conversion_data : undefined;
  }
  return undefined;
};

export const openLink = (go_link_data: GoLinkData, session: SessionPropsExtension) => {
  const trackedUrl = buildTrackedUrl(go_link_data);

  if (!trackedUrl) return;

  const matches = trackedUrl.match(/\/go\/([\w\d-]+)/);
  if (trackedUrl && matches && matches[1]) {
    window.open(trackedUrl);

    const category = go_link_data.event_category || 'Lead Click';
    const label = go_link_data.event_label || 'Page Content Link';
    const sponsorEventData = buildSponsorEventDataForGoLink(go_link_data);
    const sponsor_details = sponsorEventData ? { event_data: sponsorEventData } : undefined;
    const button_partner = generateGoLinkAction(go_link_data);
    const paid_media_data = { ...go_link_data.tracking };
    const paidMediaTrackingData = getTrackingParamForSubId(paid_media_data);

    session.getManager(TrackingManager).trackLinkEvent('click', {
      button_id: label,
      button_label: go_link_data.text || undefined,
      button_location: go_link_data.button_location ?? 'go_link',
      button_partner,
      button_type: 'go_link',
      go_link_id: go_link_data.go_link_id ?? matches[1],
      link_type: category,
      link_url: trackedUrl,
      outbound_ad_url: trackedUrl,
      paid_media_data: paidMediaTrackingData,
      sponsor_details,
    });

    return true;
  }

  return false;
};

export const extractGoLinkSlugs = (links: ArrayLike<{ href: string }>): Record<string, number> => {
  const list: Record<string, number> = {};
  for (let index = 0; index < links.length; index++) {
    const matches = links[index]?.href?.match(GO_LINK_SLUG_RE);
    const slug = matches?.[1];
    if (!slug) continue;
    list[slug] = (list[slug] ?? 0) + 1;
  }
  return list;
};

export const buildGoLinkClickData = (
  origin: HTMLAnchorElement,
  apiMatch: GoLinkData | undefined,
  context: { nodeId?: number; postId?: number },
): GoLinkData => {
  const goLinkData = extractGoLinkDataFromAnchor(apiMatch ?? {}, origin);
  goLinkData.href = origin.href;
  goLinkData.post_id = context.postId;
  if (context.nodeId) {
    goLinkData.nid = context.nodeId;
  }
  return goLinkData;
};

export type GoLinkClickAction = 'interstitial' | 'open';

export const resolveGoLinkClickAction = (goLinkData: GoLinkData): GoLinkClickAction =>
  goLinkData?.show_unmonetized_interstitial ? 'interstitial' : 'open';

export const openTrackedGoLink = (goLinkData: GoLinkData, session: SessionPropsExtension) =>
  openLink({ ...goLinkData, button_location: 'cms_go_link' }, session);
